import type {
  BlocksArrangement,
  BlocksRegion,
  RegionFlow,
} from './arrangementRecipe';
import type { BlocksNodePosition, CellFill } from './layoutRecipe';
import { localeCompare, type Locale } from '@ardoq/locale';

/** A child of the arranged group, reduced to what a region can match on. */
export type ArrangeableNode = {
  id: string;
  label: string;
  componentType: string | null;
  isGroup: boolean;
  /** The nearest hierarchy ancestor among the same siblings, if any. */
  parentId: string | null;
  /** Labels up the component hierarchy, nearest first. */
  ancestorLabels: string[];
  /** Siblings connected to this node by a reference, either way, with the reference type name. */
  related: RelatedNode[];
};

export type RelatedNode = {
  nodeId: string;
  referenceType: string;
  /** Whether the reference runs from this node to the other. */
  outgoing: boolean;
  /** Whether the reference reaches a block nested inside that sibling rather than the sibling itself. */
  nested: boolean;
};

export type Box = {
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
};
type Extent = { cols: number; rows: number };
type Demand = { start: number; span: number; need: number };

type SequenceFlow = Exclude<RegionFlow, 'tree'>;
type LocalCell = { nodeId: string; col: number; row: number; colSpan: number };
type RegionLayout = { extent: Extent; cells: LocalCell[] };

type PlacedRegion = {
  region: BlocksRegion;
  area: Box;
  nodes: ArrangeableNode[];
  layout: RegionLayout;
  alignment: PlacedAlignment | null;
};

/** A region lined up with the blocks it relates to in another, already placed, region. */
type PlacedAlignment = {
  target: PlacedRegion;
  side: AlignSide;
  /** Each anchored node's block in the target region. */
  anchors: Map<string, RelatedNode>;
};

/** Where an aligned region's area sits relative to the region it aligns with. */
export type AlignSide = 'below' | 'above' | 'right' | 'left';

const EMPTY_AREA = '.';

const toTemplateRows = (areas: string[]): string[][] =>
  areas
    .map(line => line.trim().split(/\s+/).filter(Boolean))
    .filter(cells => cells.length > 0);

type Bounds = { c0: number; c1: number; r0: number; r1: number };

const extendBounds = (
  bounds: Bounds | undefined,
  col: number,
  row: number
): Bounds =>
  bounds
    ? {
        c0: Math.min(bounds.c0, col),
        c1: Math.max(bounds.c1, col),
        r0: Math.min(bounds.r0, row),
        r1: Math.max(bounds.r1, row),
      }
    : { c0: col, c1: col, r0: row, r1: row };

/** Each area's bounding box, in template cells. A non-rectangular area gets its bounding box. */
export const parseTemplateAreas = (areas: string[]): Map<string, Box> => {
  const boundsByName = toTemplateRows(areas).reduce(
    (acc, cells, row) =>
      cells.reduce((inner, name, col) => {
        if (name === EMPTY_AREA) return inner;
        return new Map(inner).set(
          name,
          extendBounds(inner.get(name), col, row)
        );
      }, acc),
    new Map<string, Bounds>()
  );
  return new Map(
    [...boundsByName].map(([name, { c0, c1, r0, r1 }]) => [
      name,
      { col: c0, row: r0, colSpan: c1 - c0 + 1, rowSpan: r1 - r0 + 1 },
    ])
  );
};

const templateSize = (areas: string[]): Extent => {
  const rows = toTemplateRows(areas);
  return {
    cols: rows.reduce((max, cells) => Math.max(max, cells.length), 0),
    rows: rows.length,
  };
};

const sameName = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** A group named after a type, as grouping by type builds, stands for that type. */
const typeNameOf = (node: ArrangeableNode): string | null =>
  node.componentType ?? (node.isGroup ? node.label : null);

const matchesRegion = (
  region: BlocksRegion,
  node: ArrangeableNode
): boolean => {
  const typeName = typeNameOf(node);
  return (
    region.labels.some(label => sameName(label, node.label)) ||
    region.descendantsOf.some(
      label =>
        sameName(label, node.label) ||
        node.ancestorLabels.some(ancestor => sameName(label, ancestor))
    ) ||
    (typeName !== null &&
      region.componentTypes.some(type => sameName(type, typeName)))
  );
};

const regionFor = (
  regions: BlocksRegion[],
  node: ArrangeableNode
): BlocksRegion | undefined =>
  regions.find(region => matchesRegion(region, node)) ??
  regions.find(region => region.remaining);

type NodeSorter = (
  region: BlocksRegion,
  nodes: ArrangeableNode[]
) => ArrangeableNode[];

/** Tarjan's strongly connected components: each cycle as one component, every other node alone. */
const stronglyConnected = (
  ids: string[],
  successors: Map<string, string[]>
): string[][] => {
  const index = new Map<string, number>();
  const lowLink = new Map<string, number>();
  const stack: string[] = [];
  const onStack = new Set<string>();
  const components: string[][] = [];
  const visit = (id: string): void => {
    index.set(id, index.size);
    lowLink.set(id, index.get(id)!);
    stack.push(id);
    onStack.add(id);
    successors.get(id)!.forEach(next => {
      if (!index.has(next)) {
        visit(next);
        lowLink.set(id, Math.min(lowLink.get(id)!, lowLink.get(next)!));
      } else if (onStack.has(next)) {
        lowLink.set(id, Math.min(lowLink.get(id)!, index.get(next)!));
      }
    });
    if (lowLink.get(id) !== index.get(id)) return;
    const component: string[] = [];
    const popUntil = (): void => {
      const member = stack.pop()!;
      onStack.delete(member);
      component.push(member);
      if (member !== id) popUntil();
    };
    popUntil();
    components.push(component);
  };
  ids.forEach(id => {
    if (!index.has(id)) visit(id);
  });
  return components;
};

/**
 * Along the references between these nodes, sources first, with ties in label order. A cycle is
 * kept together, its nodes in label order, at the point in the flow where it begins.
 */
const sortByFlow = (
  nodes: ArrangeableNode[],
  byLabel: (a: ArrangeableNode, b: ArrangeableNode) => number
): ArrangeableNode[] => {
  const byId = new Map(nodes.map(node => [node.id, node]));
  const successors = new Map(
    nodes.map(node => [
      node.id,
      [
        ...new Set(
          node.related
            .filter(
              ({ nodeId, outgoing }) =>
                outgoing && byId.has(nodeId) && nodeId !== node.id
            )
            .map(({ nodeId }) => nodeId)
        ),
      ],
    ])
  );
  const components = stronglyConnected(
    nodes.map(({ id }) => id),
    successors
  ).map(ids => ids.map(id => byId.get(id)!).sort(byLabel));
  const componentOf = new Map(
    components.flatMap((members, component) =>
      members.map(({ id }) => [id, component] as const)
    )
  );
  const next = components.map(members => [
    ...new Set(
      members.flatMap(({ id }) =>
        successors
          .get(id)!
          .map(successor => componentOf.get(successor)!)
          .filter(component => component !== componentOf.get(id))
      )
    ),
  ]);
  const incoming = components.map(() => 0);
  next.forEach(targets => targets.forEach(target => incoming[target]++));
  const byFirstLabel = (a: number, b: number) =>
    byLabel(components[a][0], components[b][0]);
  const ready = components
    .map((_, component) => component)
    .filter(component => incoming[component] === 0)
    .sort(byFirstLabel);
  const ordered: number[] = [];
  while (ready.length) {
    const component = ready.shift()!;
    ordered.push(component);
    next[component].forEach(target => {
      incoming[target]--;
      if (incoming[target] === 0) {
        ready.push(target);
        ready.sort(byFirstLabel);
      }
    });
  }
  return ordered.flatMap(component => components[component]);
};

const toNodeSorter =
  (locale: Locale): NodeSorter =>
  (region, nodes) => {
    const byLabel = (a: ArrangeableNode, b: ArrangeableNode) =>
      localeCompare(a.label, b.label, locale);
    if (region.sortBy === 'label') return [...nodes].sort(byLabel);
    if (region.sortBy === 'flow') return sortByFlow(nodes, byLabel);
    return nodes;
  };

const gridColumns = (count: number, templateArea: Box): number =>
  Math.min(
    count,
    Math.max(
      1,
      Math.round(
        Math.sqrt((count * templateArea.colSpan) / templateArea.rowSpan)
      )
    )
  );

type FixedTracks = { columns: number | null; rows: number | null };

const fixedGridColumns = (
  count: number,
  { columns, rows }: FixedTracks
): number | null => {
  if (columns !== null) return Math.min(columns, count);
  if (rows !== null) return Math.ceil(count / Math.min(rows, count));
  return null;
};

/** Longest single line a row or column flow keeps before wrapping. */
const MAX_LINE_LENGTH = 12;
/** A wrapped row is about twice as wide as tall: landscape, so labels stay readable. */
const WRAP_ASPECT = 2;

/**
 * How many blocks a row flow puts across (or a column flow down) before wrapping: the fixed track
 * count when given, otherwise one line up to `MAX_LINE_LENGTH`, then a landscape block.
 */
const lineLength = (
  flow: 'row' | 'column',
  count: number,
  { columns, rows }: FixedTracks
): number => {
  const fixed = flow === 'row' ? columns : rows;
  if (fixed !== null) return Math.min(fixed, count);
  if (count <= MAX_LINE_LENGTH) return count;
  return Math.ceil(Math.sqrt(count * WRAP_ASPECT));
};

const extentOf = (
  flow: SequenceFlow,
  count: number,
  templateArea: Box,
  fixed: FixedTracks
): Extent => {
  if (count <= 1) return { cols: 1, rows: 1 };
  switch (flow) {
    case 'row': {
      const cols = lineLength('row', count, fixed);
      return { cols, rows: Math.ceil(count / cols) };
    }
    case 'column': {
      const rows = lineLength('column', count, fixed);
      return { cols: Math.ceil(count / rows), rows };
    }
    case 'grid': {
      const cols =
        fixedGridColumns(count, fixed) ?? gridColumns(count, templateArea);
      return { cols, rows: Math.ceil(count / cols) };
    }
  }
};

const sumRange = (sizes: number[], start: number, span: number): number =>
  sizes.slice(start, start + span).reduce((sum, size) => sum + size, 0);

/** Spreads a shortfall over the tracks an area spans, the later tracks taking the remainder. */
const satisfyDemand = (
  sizes: number[],
  { start, span, need }: Demand
): number[] => {
  const shortfall = need - sumRange(sizes, start, span);
  if (shortfall <= 0) return sizes;
  const share = Math.floor(shortfall / span);
  const remainder = shortfall % span;
  return sizes.map((size, index) => {
    const offset = index - start;
    if (offset < 0 || offset >= span) return size;
    return size + share + (offset >= span - remainder ? 1 : 0);
  });
};

/** Logical cells per template track, narrowest areas first so a spanning area only takes what is left. */
const sizeTracks = (count: number, demands: Demand[]): number[] =>
  [...demands]
    .sort((a, b) => a.span - b.span)
    .reduce(
      satisfyDemand,
      Array.from({ length: count }, () => 1)
    );

const toOffsets = (sizes: number[]): number[] =>
  sizes.map((_, index) => sumRange(sizes, 0, index));

const toLogicalArea = (
  area: Box,
  { colOffsets, rowOffsets, colSizes, rowSizes }: TrackLayout
): Box => ({
  col: colOffsets[area.col],
  row: rowOffsets[area.row],
  colSpan: sumRange(colSizes, area.col, area.colSpan),
  rowSpan: sumRange(rowSizes, area.row, area.rowSpan),
});

type TrackLayout = {
  colSizes: number[];
  rowSizes: number[];
  colOffsets: number[];
  rowOffsets: number[];
};

const cellWithinExtent = (
  flow: SequenceFlow,
  index: number,
  extent: Extent
): { col: number; row: number } => {
  switch (flow) {
    case 'row':
      return { col: index % extent.cols, row: Math.floor(index / extent.cols) };
    case 'column':
      return { col: Math.floor(index / extent.rows), row: index % extent.rows };
    case 'grid':
      return { col: index % extent.cols, row: Math.floor(index / extent.cols) };
  }
};

const layoutSequence = (
  flow: SequenceFlow,
  nodes: ArrangeableNode[],
  templateArea: Box,
  fixed: FixedTracks
): RegionLayout => {
  const extent = extentOf(flow, nodes.length, templateArea, fixed);
  return {
    extent,
    cells: nodes.map((node, index) => ({
      nodeId: node.id,
      colSpan: 1,
      ...cellWithinExtent(flow, index, extent),
    })),
  };
};

const MAX_STACK_HEIGHT = 8;

type ChildrenOf = (node: ArrangeableNode) => ArrangeableNode[];

/** Leaves stacked down and wrapped across, every column at most `MAX_STACK_HEIGHT` tall. */
const stackLeaves = (leaves: ArrangeableNode[]): RegionLayout => {
  const cols = Math.max(1, Math.ceil(leaves.length / MAX_STACK_HEIGHT));
  const height = Math.ceil(leaves.length / cols);
  return {
    extent: { cols, rows: height },
    cells: leaves.map((node, index) => ({
      nodeId: node.id,
      col: Math.floor(index / height),
      row: index % height,
      colSpan: 1,
    })),
  };
};

/** Consecutive leaves share one stack; each child with children of its own gets a block. */
const childBlocks = (
  childrenOf: ChildrenOf,
  children: ArrangeableNode[]
): RegionLayout[] => {
  const runs = children.reduce<ArrangeableNode[][]>((acc, child) => {
    const last = acc.at(-1);
    const isLeaf = childrenOf(child).length === 0;
    if (isLeaf && last && childrenOf(last[0]).length === 0) {
      return [...acc.slice(0, -1), [...last, child]];
    }
    return [...acc, [child]];
  }, []);
  return runs.map(run =>
    childrenOf(run[0]).length === 0
      ? stackLeaves(run)
      : layoutSubtree(childrenOf, run[0])
  );
};

/**
 * A node spanning a row, its children side by side beneath it, recursively: so every child sits
 * under its own parent, and the layout shows each parent/child link without drawing it.
 */
const layoutSubtree = (
  childrenOf: ChildrenOf,
  node: ArrangeableNode
): RegionLayout => {
  const blocks = childBlocks(childrenOf, childrenOf(node));
  if (blocks.length === 0) {
    return {
      extent: { cols: 1, rows: 1 },
      cells: [{ nodeId: node.id, col: 0, row: 0, colSpan: 1 }],
    };
  }
  const offsets = toOffsets(blocks.map(block => block.extent.cols));
  const cols = blocks.reduce((sum, block) => sum + block.extent.cols, 0);
  return {
    extent: {
      cols,
      rows:
        1 + blocks.reduce((max, block) => Math.max(max, block.extent.rows), 0),
    },
    cells: [
      { nodeId: node.id, col: 0, row: 0, colSpan: cols },
      ...blocks.flatMap((block, index) =>
        block.cells.map(cell => ({
          ...cell,
          col: cell.col + offsets[index],
          row: cell.row + 1,
        }))
      ),
    ],
  };
};

type LayoutTreeArgs = {
  region: BlocksRegion;
  nodes: ArrangeableNode[];
  sortNodes: NodeSorter;
};

const layoutTree = ({
  region,
  nodes,
  sortNodes,
}: LayoutTreeArgs): RegionLayout => {
  const ids = new Set(nodes.map(node => node.id));
  const childrenByParentId = Map.groupBy(nodes, node =>
    node.parentId !== null && ids.has(node.parentId) ? node.parentId : null
  );
  const childrenOf = (node: ArrangeableNode): ArrangeableNode[] =>
    sortNodes(region, childrenByParentId.get(node.id) ?? []);
  const trees = sortNodes(region, childrenByParentId.get(null) ?? []).map(
    root => layoutSubtree(childrenOf, root)
  );
  const offsets = toOffsets(trees.map(tree => tree.extent.cols));
  return {
    extent: {
      cols: trees.reduce((sum, tree) => sum + tree.extent.cols, 0),
      rows: trees.reduce((max, tree) => Math.max(max, tree.extent.rows), 0),
    },
    cells: trees.flatMap((tree, index) =>
      tree.cells.map(cell => ({ ...cell, col: cell.col + offsets[index] }))
    ),
  };
};

type LayoutRegionArgs = LayoutTreeArgs & { templateArea: Box };

const layoutRegion = ({
  region,
  nodes,
  templateArea,
  sortNodes,
}: LayoutRegionArgs): RegionLayout =>
  region.flow === 'tree'
    ? layoutTree({ region, nodes, sortNodes })
    : layoutSequence(region.flow, nodes, templateArea, {
        columns: region.columns,
        rows: region.rows,
      });

/** Stretch applies to a single line only: a wrapped flow's blocks sit side by side. */
const fillFor = (region: BlocksRegion, extent: Extent): CellFill | null => {
  if (!region.stretch) return null;
  if (region.flow === 'column' && extent.cols === 1) return 'across';
  if (region.flow === 'row' && extent.rows === 1) return 'down';
  return null;
};

const placeRegion = (
  { region, nodes, layout: { extent, cells } }: PlacedRegion,
  area: Box
): BlocksNodePosition[] => {
  const fill = fillFor(region, extent);
  if (nodes.length === 1 && nodes[0].isGroup) {
    return [{ nodeId: nodes[0].id, ...area, ...(fill && { fill }) }];
  }
  const colInset =
    region.align === 'center'
      ? Math.floor((area.colSpan - extent.cols) / 2)
      : 0;
  const rowInset =
    region.align === 'center'
      ? Math.floor((area.rowSpan - extent.rows) / 2)
      : 0;
  const stretchAcross = fill === 'across';
  const stretchDown = fill === 'down';
  return cells.map(({ nodeId, col, row, colSpan }) => ({
    nodeId,
    col: stretchAcross ? area.col : area.col + colInset + col,
    row: stretchDown ? area.row : area.row + rowInset + row,
    colSpan: stretchAcross ? area.colSpan : colSpan,
    rowSpan: stretchDown ? area.rowSpan : 1,
    ...(fill && { fill }),
  }));
};

type ClaimedRegions = {
  areasByName: Map<string, Box>;
  regions: BlocksRegion[];
  nodesByRegion: Map<BlocksRegion | undefined, ArrangeableNode[]>;
};

const claimNodes = (
  arrangement: BlocksArrangement,
  children: ArrangeableNode[]
): ClaimedRegions => {
  const areasByName = parseTemplateAreas(arrangement.areas);
  const regions = arrangement.regions.filter(region =>
    areasByName.has(region.name)
  );
  return {
    areasByName,
    regions,
    nodesByRegion: Map.groupBy(children, node => regionFor(regions, node)),
  };
};

export type CompileArrangementArgs = {
  arrangement: BlocksArrangement;
  children: ArrangeableNode[];
  locale: Locale;
};

/** Whether a reference type is one an alignWith allows: any, when it names none. */
export const sameReferenceType = (
  allowed: string[],
  referenceType: string
): boolean =>
  allowed.length === 0 || allowed.some(name => sameName(name, referenceType));

/**
 * The reference to the first block, in the other region's order, that a node is connected to by an
 * allowed reference, preferring a direct reference to that block over one into its contents.
 */
const anchorOf = (
  node: ArrangeableNode,
  targetNodes: ArrangeableNode[],
  referenceTypes: string[]
): RelatedNode | null => {
  const order = new Map(targetNodes.map(({ id }, index) => [id, index]));
  const [anchor] = node.related
    .filter(
      ({ nodeId, referenceType }) =>
        order.has(nodeId) && sameReferenceType(referenceTypes, referenceType)
    )
    .sort(
      (a, b) =>
        order.get(a.nodeId)! - order.get(b.nodeId)! ||
        Number(a.nested) - Number(b.nested)
    );
  return anchor ?? null;
};

const contains = (
  outerStart: number,
  outerSpan: number,
  innerStart: number,
  innerSpan: number
) =>
  outerStart <= innerStart && innerStart + innerSpan <= outerStart + outerSpan;

/**
 * Which side of the target's template area this area is on: below or above when it spans all of
 * the target's columns, beside it when it spans all of its rows.
 */
const sideOf = (area: Box, target: Box): AlignSide | null => {
  const spansCols = contains(
    area.col,
    area.colSpan,
    target.col,
    target.colSpan
  );
  const spansRows = contains(
    area.row,
    area.rowSpan,
    target.row,
    target.rowSpan
  );
  if (spansCols && area.row >= target.row + target.rowSpan) return 'below';
  if (spansCols && area.row + area.rowSpan <= target.row) return 'above';
  if (spansRows && area.col >= target.col + target.colSpan) return 'right';
  if (spansRows && area.col + area.colSpan <= target.col) return 'left';
  return null;
};

const isVertical = (side: AlignSide): boolean =>
  side === 'below' || side === 'above';

/**
 * Identifies the final column (or row) a region's block lands in. An aligned region's local lines
 * don't: its anchored blocks take their anchor's line, and its unrelated blocks are numbered apart.
 */
const finalLineKey = (
  region: PlacedRegion,
  nodeId: string,
  vertical: boolean
): string => {
  const cell = region.layout.cells.find(other => other.nodeId === nodeId);
  const local = String(vertical ? cell?.col : cell?.row);
  const alignment = region.alignment;
  if (!alignment || isVertical(alignment.side) !== vertical) return local;
  const anchor = alignment.anchors.get(nodeId);
  return anchor
    ? `anchored ${finalLineKey(alignment.target, anchor.nodeId, vertical)}`
    : `unrelated ${local}`;
};

/**
 * Each node lined up with the block it is connected to in the other region: stacked in that
 * block's column when the regions are stacked, nearest first, or in its row when they are side by
 * side. Nodes connected to none of them are stacked after, in lines of their own.
 */
const layoutAligned = (
  nodes: ArrangeableNode[],
  target: PlacedRegion,
  referenceTypes: string[],
  side: AlignSide
): { layout: RegionLayout; alignment: PlacedAlignment } => {
  const vertical = isVertical(side);
  const lineByTargetId = new Map(
    target.layout.cells.map(({ nodeId, col, row }) => [
      nodeId,
      vertical ? col : row,
    ])
  );
  const anchors = new Map(
    nodes.flatMap(node => {
      const anchor = anchorOf(node, target.nodes, referenceTypes);
      return anchor ? [[node.id, anchor] as const] : [];
    })
  );
  const lines = Map.groupBy(
    nodes.filter(({ id }) => anchors.has(id)),
    ({ id }) => finalLineKey(target, anchors.get(id)!.nodeId, vertical)
  );
  const depth = Math.max(0, ...[...lines.values()].map(line => line.length));
  const awayFromTarget = side === 'below' || side === 'right';
  const anchoredCells = [...lines.values()].flatMap(stacked =>
    stacked.map(({ id }, index) => {
      const step = awayFromTarget ? index : depth - 1 - index;
      const line = lineByTargetId.get(anchors.get(id)!.nodeId)!;
      return vertical
        ? { nodeId: id, col: line, row: step, colSpan: 1 }
        : { nodeId: id, col: step, row: line, colSpan: 1 };
    })
  );
  const loose = stackLeaves(nodes.filter(({ id }) => !anchors.has(id)));
  const hasLoose = loose.cells.length > 0;
  const targetLines = vertical
    ? target.layout.extent.cols
    : target.layout.extent.rows;
  const extent = vertical
    ? {
        cols: targetLines + (hasLoose ? loose.extent.cols : 0),
        rows: Math.max(1, depth, hasLoose ? loose.extent.rows : 0),
      }
    : {
        cols: Math.max(1, depth, hasLoose ? loose.extent.cols : 0),
        rows: targetLines + (hasLoose ? loose.extent.rows : 0),
      };
  return {
    alignment: { target, side, anchors },
    layout: { extent, cells: [...anchoredCells, ...loose.cells] },
  };
};

type Alignment = { target: BlocksRegion; side: AlignSide };

/**
 * Every region in dependency order, so a region can align with one that is itself aligned, each
 * with the region and side it aligns with. A region that can't align gets null and keeps its own
 * flow: one aligned diagonally, or not spanning the target's columns or rows, a missing target, or
 * a cycle.
 */
const resolveAlignments = (
  regions: BlocksRegion[],
  areasByName: Map<string, Box>
): Map<BlocksRegion, Alignment | null> => {
  const targetOf = ({ alignWith }: BlocksRegion) =>
    alignWith && regions.find(other => sameName(other.name, alignWith.region));
  const done = new Map<BlocksRegion, Alignment | null>(
    regions.filter(({ alignWith }) => !alignWith).map(region => [region, null])
  );
  const resolve = (pending: BlocksRegion[]): BlocksRegion[] => {
    const ready = pending.filter(region => {
      const target = targetOf(region);
      return target && done.has(target);
    });
    ready.forEach(region => {
      const target = targetOf(region)!;
      const side = sideOf(
        areasByName.get(region.name)!,
        areasByName.get(target.name)!
      );
      done.set(region, side && { target, side });
    });
    const rest = pending.filter(region => !ready.includes(region));
    return ready.length === 0 ? rest : resolve(rest);
  };
  resolve(regions.filter(({ alignWith }) => alignWith)).forEach(region =>
    done.set(region, null)
  );
  return done;
};

const collectRegions = ({
  arrangement,
  children,
  locale,
}: CompileArrangementArgs): PlacedRegion[] => {
  const { areasByName, regions, nodesByRegion } = claimNodes(
    arrangement,
    children
  );
  const sortNodes = toNodeSorter(locale);
  const withNodes = regions.filter(region => nodesByRegion.has(region));
  const placed = new Map<BlocksRegion, PlacedRegion>();
  resolveAlignments(withNodes, areasByName).forEach((alignment, region) => {
    const area = areasByName.get(region.name)!;
    const nodes = sortNodes(region, nodesByRegion.get(region)!);
    const target = alignment && placed.get(alignment.target);
    placed.set(
      region,
      alignment && target
        ? {
            region,
            area,
            nodes,
            ...layoutAligned(
              nodes,
              target,
              region.alignWith!.referenceTypes,
              alignment.side
            ),
          }
        : {
            region,
            area,
            nodes,
            layout: layoutRegion({
              region,
              nodes,
              templateArea: area,
              sortNodes,
            }),
            alignment: null,
          }
    );
  });
  return [...placed.values()];
};

const toTrackLayout = (
  arrangement: BlocksArrangement,
  placed: PlacedRegion[]
): TrackLayout => {
  const size = templateSize(arrangement.areas);
  const colSizes = sizeTracks(
    size.cols,
    placed.map(({ area, layout: { extent } }) => ({
      start: area.col,
      span: area.colSpan,
      need: extent.cols,
    }))
  );
  const rowSizes = sizeTracks(
    size.rows,
    placed.map(({ area, layout: { extent } }) => ({
      start: area.row,
      span: area.rowSpan,
      need: extent.rows,
    }))
  );
  return {
    colSizes,
    rowSizes,
    colOffsets: toOffsets(colSizes),
    rowOffsets: toOffsets(rowSizes),
  };
};

type PositionsById = Map<string, BlocksNodePosition>;

type Line = { start: number; span: number };

const lineOf = (
  vertical: boolean,
  { col, row, colSpan, rowSpan }: Box
): Line =>
  vertical ? { start: col, span: colSpan } : { start: row, span: rowSpan };

const range = (start: number, end: number): number[] =>
  Array.from({ length: Math.max(0, end - start) }, (_, index) => start + index);

/**
 * Lines of the area no target block is in, for nodes with no related block: after the target's
 * blocks first, then before them nearest first, then past the area if those run out.
 */
const freeLines = (area: Line, occupied: Line[], count: number): number[] => {
  const first = Math.min(...occupied.map(({ start }) => start));
  const last = Math.max(...occupied.map(({ start, span }) => start + span));
  const areaEnd = area.start + area.span;
  const beyond = Math.max(areaEnd, last);
  return [
    ...range(last, areaEnd),
    ...range(area.start, first).reverse(),
    ...range(beyond, beyond + count),
  ].slice(0, count);
};

/**
 * An aligned region's cells in the group's grid: each anchored node in its anchor's final column
 * or row, the others in lines of the area no target block is in.
 */
const placeAligned = (
  { region, layout: { extent, cells } }: PlacedRegion,
  { target, side, anchors }: PlacedAlignment,
  byId: PositionsById,
  area: Box
): BlocksNodePosition[] => {
  const vertical = isVertical(side);
  const targetLines = target.nodes.flatMap(({ id }) => {
    const position = byId.get(id);
    return position ? [lineOf(vertical, position)] : [];
  });
  const looseCount =
    (vertical ? extent.cols : extent.rows) -
    (vertical ? target.layout.extent.cols : target.layout.extent.rows);
  const looseLines = freeLines(
    lineOf(vertical, area),
    targetLines,
    Math.max(0, looseCount)
  );
  const across = lineOf(!vertical, area);
  const depth = vertical ? extent.rows : extent.cols;
  const inset =
    region.align === 'center' ? Math.floor((across.span - depth) / 2) : 0;
  return cells.map(({ nodeId, col, row }) => {
    const related = anchors.get(nodeId);
    const anchor = related && byId.get(related.nodeId);
    const [line, step] = vertical ? [col, row] : [row, col];
    const at = anchor ? lineOf(vertical, anchor).start : looseLines[line];
    const away = across.start + inset + step;
    return vertical
      ? { nodeId, col: at, row: away, colSpan: 1, rowSpan: 1 }
      : { nodeId, col: away, row: at, colSpan: 1, rowSpan: 1 };
  });
};

/**
 * Logical cells, in the arranged group's own grid, for every child a region
 * claimed. Children no region claimed get no cell, so the layout places them
 * below the arrangement.
 */
export const compileArrangement = (
  args: CompileArrangementArgs
): BlocksNodePosition[] => {
  const placed = collectRegions(args);
  const tracks = toTrackLayout(args.arrangement, placed);
  const byId: PositionsById = new Map();
  return placed.flatMap(region => {
    const area = toLogicalArea(region.area, tracks);
    const positions = region.alignment
      ? placeAligned(region, region.alignment, byId, area)
      : placeRegion(region, area);
    positions.forEach(position => byId.set(position.nodeId, position));
    return positions;
  });
};

/** The block a node lines up with, and whether its reference is to something inside that block. */
export type ResolvedAnchor = { block: ArrangeableNode; nested: boolean };

export type ResolvedRegion = {
  region: BlocksRegion;
  /** Where the region sits in the template. */
  area: Box;
  /** In the order the region lays them out. */
  nodes: ArrangeableNode[];
  /** For an aligned region, the side it lines up on and what each anchored node lines up with. */
  alignment: {
    side: AlignSide;
    /** The blocks of the region it lines up with, in that region's order. */
    targetNodes: ArrangeableNode[];
    anchors: Map<string, ResolvedAnchor>;
  } | null;
};

export type ResolvedArrangement = {
  arrangement: BlocksArrangement;
  children: ArrangeableNode[];
  /** Each region that holds nodes, in recipe order. */
  regions: ResolvedRegion[];
};

const toResolvedAnchors = ({
  target,
  anchors,
}: PlacedAlignment): Map<string, ResolvedAnchor> => {
  const blockById = new Map(target.nodes.map(node => [node.id, node]));
  return new Map(
    [...anchors].flatMap(([nodeId, { nodeId: blockId, nested }]) => {
      const block = blockById.get(blockId);
      return block ? [[nodeId, { block, nested }] as const] : [];
    })
  );
};

/** How the compiler lays these children out, region by region, for describing the result. */
export const resolveArrangement = (
  args: CompileArrangementArgs
): ResolvedArrangement => {
  const placed = new Map(
    collectRegions(args).map(placedRegion => [
      placedRegion.region,
      placedRegion,
    ])
  );
  return {
    arrangement: args.arrangement,
    children: args.children,
    regions: args.arrangement.regions.flatMap(recipeRegion => {
      const placedRegion = placed.get(recipeRegion);
      if (!placedRegion) return [];
      const { region, area, nodes, alignment } = placedRegion;
      return [
        {
          region,
          area,
          nodes,
          alignment: alignment && {
            side: alignment.side,
            targetNodes: alignment.target.nodes,
            anchors: toResolvedAnchors(alignment),
          },
        },
      ];
    }),
  };
};

const MAX_OUTCOME_GROUP_LABELS = 10;

export type ArrangementOutcome = {
  /**
   * Each region that holds nodes, with the labels of the groups among them, and for a region
   * aligned with related blocks, how many had no related block to line up with. `unrelated` is
   * null for a region that doesn't align, including one whose alignWith couldn't be applied.
   */
  regions: {
    name: string;
    count: number;
    groups: string[];
    unrelated: number | null;
  }[];
  /** How many of those only arrived through a `remaining` region, matching no rule of their own. */
  viaRemaining: number;
  unplaced: number;
};

/** A parent and child whose link a layout already shows. */
export type ShownLink = { parentId: string; childId: string };

const shownInRegion = (nodes: ArrangeableNode[]): ShownLink[] => {
  const ids = new Set(nodes.map(({ id }) => id));
  return nodes.flatMap(({ id, parentId }) =>
    parentId !== null && ids.has(parentId) ? [{ parentId, childId: id }] : []
  );
};

/**
 * The parent/child links a 'tree' region's layout shows: every node sits beneath its parent when
 * both are in the region. A parent in another region is not drawn as one, so that link doesn't count.
 */
export const treeShownLinks = (
  arrangement: BlocksArrangement,
  children: ArrangeableNode[]
): ShownLink[] => {
  const { regions, nodesByRegion } = claimNodes(arrangement, children);
  return regions
    .filter(({ flow }) => flow === 'tree')
    .flatMap(region => shownInRegion(nodesByRegion.get(region) ?? []));
};

/** How many of an aligned region's nodes have no related block to line up with. */
const countUnrelated = ({
  region,
  nodes,
  alignment,
}: ResolvedRegion): number | null => {
  if (!alignment || !region.alignWith) return null;
  return nodes.filter(({ id }) => !alignment.anchors.has(id)).length;
};

/** What an arrangement did with these children, for the assistant to read back. */
export const summarizeArrangement = ({
  children,
  regions: resolved,
}: ResolvedArrangement): ArrangementOutcome => {
  const regions = resolved.map(resolvedRegion => ({
    name: resolvedRegion.region.name,
    count: resolvedRegion.nodes.length,
    groups: resolvedRegion.nodes
      .filter(node => node.isGroup)
      .slice(0, MAX_OUTCOME_GROUP_LABELS)
      .map(node => node.label),
    unrelated: countUnrelated(resolvedRegion),
  }));
  const hasRemaining = resolved.some(({ region }) => region.remaining);
  const placedCount = regions.reduce((sum, { count }) => sum + count, 0);
  return {
    regions,
    viaRemaining: hasRemaining
      ? children.filter(
          node => !resolved.some(({ region }) => matchesRegion(region, node))
        ).length
      : 0,
    unplaced: children.length - placedCount,
  };
};

const CHILD_AREA = 'children';

/**
 * One arrangement per group a region with a `childLayout` claims, laying out that group's own
 * children in a single area.
 */
export const toChildArrangements = (
  arrangement: BlocksArrangement,
  children: ArrangeableNode[]
): BlocksArrangement[] => {
  const { regions, nodesByRegion } = claimNodes(arrangement, children);
  return regions.flatMap(region => {
    const layout = region.childLayout;
    if (!layout) return [];
    return (nodesByRegion.get(region) ?? [])
      .filter(node => node.isGroup)
      .map(group => ({
        containerId: group.id,
        areas: [CHILD_AREA],
        regions: [
          {
            name: CHILD_AREA,
            componentTypes: [],
            labels: [],
            descendantsOf: [],
            remaining: true,
            flow: layout.flow,
            align: layout.align,
            sortBy: layout.sortBy,
            columns: layout.columns,
            rows: layout.rows,
            stretch: false,
            childLayout: null,
            alignWith: null,
          },
        ],
      }));
  });
};
